import { useAuth } from "@/providers/auth/hooks";
import { useOrg } from "@/shared/domain/orgs/hooks";
import { EntityIcon } from "@/shared/foundation/icons";
import { Banner } from "@/shared/foundation/primitives";

export interface OtherOrgBannerProps {
  /** The org that owns what the page shows. */
  orgId: string;
  /**
   * The sentence the banner says, given both orgs' display names: the page
   * knows what it shows and what the reader can do with it from here.
   */
  children: (orgs: { orgName: string; currentOrgName: string }) => string;
  /** Applied to the banner, so spacing around it goes away with it. */
  className?: string;
}

/**
 * A note that what the page shows belongs to another org than the one the
 * reader is working in, with a button that switches to that org.
 *
 * Renders nothing when `orgId` is the current org, when there is no current
 * org, or until the other org's name is known. The other org is looked up
 * among the reader's memberships first; a Roboto admin can open a resource in
 * an org they do not belong to, so failing that it is read by id, as
 * `CheckOutOrgButton` does. A name is the org's `name`, or its id when it has
 * none.
 */
export function OtherOrgBanner({
  orgId,
  children,
  className,
}: OtherOrgBannerProps) {
  const {
    currentOrganization,
    availableOrganizations,
    setCurrentOrganization,
  } = useAuth();
  const isOtherOrg =
    currentOrganization !== null && orgId !== currentOrganization.org_id;
  const memberOrg = availableOrganizations?.find((org) => org.org_id === orgId);
  const orgQuery = useOrg(
    isOtherOrg && memberOrg === undefined ? orgId : undefined,
  );
  const org = memberOrg ?? orgQuery.data;

  if (!isOtherOrg || currentOrganization === null || org === undefined) {
    return null;
  }

  const orgName = org.name || org.org_id;
  const currentOrgName = currentOrganization.name || currentOrganization.org_id;

  return (
    <Banner
      className={className}
      icon={<EntityIcon type="org" />}
      description={children({ orgName, currentOrgName })}
      action={
        <Banner.Action onClick={() => setCurrentOrganization(org)}>
          Switch to {orgName}
        </Banner.Action>
      }
    />
  );
}
